import { Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GalleryQuiltData } from "../Data/GalleryQuiltData";
import { GameType } from "../Data/GameData";
import * as SoundEffect from "../Data/SoundEffect";
import * as VoiceOver from "../Data/VoiceOver";
import { TintFilter } from "../Filters/TintFilter";
import { GalleryItem } from "../GameObjects/GalleryItem";
import { Patch } from "../GameObjects/Patch";
import { Quilt } from "../GameObjects/Quilt";

export class GalleryQuilt extends GalleryItem {
    private _quiltContainer!: PIXI.Container;
    private _quiltFront!: Quilt;
    private _quiltBack!: Quilt;
    private _quiltMask!: PIXI.Graphics;
    private _foldPerc: number;

    constructor(pCallback: (pItem: GalleryItem) => void) {
        super(GameType.QUILT, pCallback);

        this._foldPerc = 0;
        this._hoverVO = VoiceOver.QUILT_BUTTON;

        this._homePos = new PIXI.Point(30, -65);
        this._homeScale = 0.559;
        this._presentScale = 0.7;
        this._targetState = Constants.STATE_QUILT;

        this.createVisuals();

        this._visuals.position.copy(this._homePos);
    }

    protected get recentIndex(): number {
        return AppProgress.games[this._gameType].recentItemIndex;
    }

    protected get saveData(): GalleryQuiltData {
        return AppProgress.games[this._gameType].saveData[this.recentIndex] as GalleryQuiltData;
    }

    public get foldPerc(): number {
        return this._foldPerc;
    }

    public set foldPerc(pValue: number) {
        this._foldPerc = pValue;
        this._quiltMask.position.set(
            -this._quiltFront.width * this._foldPerc,
            -this._quiltFront.height * this._foldPerc,
        );

        this._quiltBack.position.set(
            this._quiltFront.width * ((1 - this._foldPerc) * 2 - 1),
            this._quiltFront.height * ((1 - this._foldPerc) * 2 - 1),
        );
    }

    public presentItem(): TimelineLite {
        this.interactive = false;
        this._visuals.position.set(0, -100);
        this._quiltContainer.scale.set(this._presentScale);
        this.foldPerc = 2;

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this, 1.5, {
            foldPerc: 0,
            ease: Sine.easeOut,
        });
        return timeline;
    }

    public moveItemToWall(): TimelineLite {
        const moveDur: number = 1.0;

        const timeline: TimelineLite = new TimelineLite();
        // x pos
        timeline.to(this._visuals, moveDur * 0.5, {
            x: this._homePos.x + 80,
            ease: Sine.easeInOut,
        }, 0);
        timeline.to(this._visuals, moveDur * 0.5, {
            x: this._homePos.x,
            ease: Sine.easeInOut,
        }, moveDur * 0.5);
        // y pos
        timeline.to(this._visuals, moveDur * 0.4, {
            y: this._homePos.y - 200,
            ease: Sine.easeInOut,
        }, 0);
        timeline.to(this._visuals, moveDur * 0.6, {
            y: this._homePos.y,
            ease: Sine.easeInOut,
        }, moveDur * 0.4);
        // scale
        timeline.to(this._quiltContainer.scale, moveDur, {
            x: this._homeScale,
            y: this._homeScale,
            ease: Sine.easeInOut,
        }, 0);
        // rotation
        timeline.to(this._visuals, moveDur * 0.35, {
            rotation: 0.03,
            ease: Sine.easeInOut,
        }, 0);
        timeline.to(this._visuals, moveDur * 0.4, {
            rotation: -0.03,
            ease: Sine.easeInOut,
        }, moveDur * 0.35);
        timeline.to(this._visuals, moveDur * 0.25, {
            rotation: 0,
            ease: Sine.easeInOut,
        }, moveDur * 0.75);
        return timeline;
    }

    public performHangUp(): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        timeline.call(() => {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                SoundEffect.HEAVY_FABRIC,
                1, false, HLF.AudioCategory.SFX,
            ));
            this._quiltBack.visible = false;
            this._quiltMask.visible = false;
            this._quiltFront.mask = null;
            this.interactive = true;
        });
        return timeline;
    }

    public cacheVisuals(): PIXI.Texture {
        this._quiltContainer.scale.set(1);
        const texture: PIXI.Texture = Application.instance.renderer.generateTexture(
            this._visuals,
            undefined,
            Application.instance.renderer.resolution,
        );
        this._quiltContainer.scale.set(this._homeScale);
        return texture;
    }

    protected createVisualDefault(): void {
        const image: PIXI.Sprite = HLF.PixiUtils.makeSprite("menu-button-quilt-bw", Constants.SPRITESHEET_MENU);
        this._visuals.addChild(image);
    }

    protected createVisualComplete(): void {
        this._quiltContainer = new PIXI.Container();
        this._visuals.addChild(this._quiltContainer);

        this._quiltFront = new Quilt(this.saveData.index);
        for (let i = 0; i < this.saveData.patches.length; ++i) {
            this._quiltFront.addPatch(new Patch(this.saveData.patches[i]), this.saveData.positions[i], true);
        }
        this._quiltContainer.addChild(this._quiltFront);

        this._quiltBack = this._quiltFront.clone();
        this._quiltBack.scale.x = -1;
        this._quiltBack.rotation = Math.PI * 0.5;
        this._quiltBack.filters = [ new TintFilter(new HLF.Colour(100, 100, 100).toWebGL()) ];
        this._quiltContainer.addChild(this._quiltBack);

        this._quiltMask = new PIXI.Graphics();
        this._quiltMask.beginFill(0x000000);
        this._quiltMask.drawPolygon([
            new PIXI.Point(-this._quiltFront.width, 0),
            new PIXI.Point(0, this._quiltFront.height),
            new PIXI.Point(this._quiltFront.width, 0),
            new PIXI.Point(0, -this._quiltFront.height),
        ]);
        this._quiltMask.endFill();
        this._quiltContainer.addChild(this._quiltMask);
        this._quiltFront.mask = this._quiltMask;
        this._quiltBack.mask = this._quiltMask;
    }

    protected createVisualCached(): void {
        super.createVisualCached();
        this._cached!.scale.set(this._homeScale);
    }
}
